import React, { useRef, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import './Home.css'

// Brand names + domains for Google favicon CDN (universally accessible, no CORS issues)
const brands = [
  { name: 'Adobe',      domain: 'adobe.com' },
  { name: 'Behance',    domain: 'behance.net' },
  { name: 'Dribbble',   domain: 'dribbble.com' },
  { name: 'Figma',      domain: 'figma.com' },
  { name: 'Notion',     domain: 'notion.so' },
  { name: 'Substack',   domain: 'substack.com' },
  { name: 'Patreon',    domain: 'patreon.com' },
  { name: 'ArtStation', domain: 'artstation.com' },
  { name: 'Spotify',    domain: 'spotify.com' },
  { name: 'SoundCloud', domain: 'soundcloud.com' },
  { name: 'YouTube',    domain: 'youtube.com' },
  { name: 'Vimeo',      domain: 'vimeo.com' },
  { name: 'GitHub',     domain: 'github.com' },
  { name: 'Blender',    domain: 'blender.org' },
  { name: 'Unity',      domain: 'unity.com' },
  { name: 'Procreate',  domain: 'procreate.com' },
]

function logoUrl(domain) {
  return `https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${domain}&size=64`
}

const faqs = [
  { q: 'What counts as evidence?', a: 'Process videos, timelapse recordings, raw project files (PSD, Procreate, FCP, Ableton, etc.), handwritten drafts, or any material that shows the creative process.' },
  { q: 'How long does verification take?', a: 'It depends on how quickly community reviewers pick up your submission — most projects get reviewed within a few days. Reviewing others first helps keep the queue moving for everyone.' },
  { q: 'What if my evidence is rejected?', a: 'Community reviewers will explain what additional evidence is needed. You can add more evidence and resubmit at no extra cost.' },
  { q: 'Can I verify physical art?', a: 'Yes. Physical artwork, sculpture, ceramics, and handcrafted items are all supported.' },
  { q: 'Is the certificate permanent?', a: 'Yes. Once verified, your certificate is permanent and publicly accessible via a unique URL and QR code.' },
  { q: 'Can I remain anonymous?', a: 'You can use a display name instead of your real name. We only verify your email for account security.' },
]

// Hero video background — pulled from Velorah
function HeroVideo() {
  const videoRef = useRef(null)
  const [muted, setMuted] = useState(true)
  const [playing, setPlaying] = useState(true)

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted
  }, [muted])

  useEffect(() => {
    if (!videoRef.current) return
    if (playing) videoRef.current.play().catch(() => {})
    else videoRef.current.pause()
  }, [playing])

  return (
    <div className="hero-video-wrap">
      <video
        ref={videoRef}
        autoPlay loop muted playsInline
        className="hero-video"
      >
        <source
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260314_131748_f2ca2a28-fed7-44c8-b9a9-bd9acdd5ec31.mp4"
          type="video/mp4"
        />
      </video>
      {/* Gradient overlay to keep content readable */}
      <div className="hero-video-overlay" />
      {/* Minimal video controls bottom-right */}
      <div className="hero-video-controls">
        <button
          className="video-ctrl-btn"
          onClick={() => setPlaying(p => !p)}
          title={playing ? 'Pause' : 'Play'}
        >
          {playing
            ? <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><rect x="2" y="1" width="3" height="10" rx="1"/><rect x="7" y="1" width="3" height="10" rx="1"/></svg>
            : <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M3 2l7 4-7 4V2z"/></svg>
          }
        </button>
        <button
          className="video-ctrl-btn"
          onClick={() => setMuted(m => !m)}
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted
            ? <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M1 4h2.5L7 1v10L3.5 8H1V4z"/><line x1="9" y1="4" x2="12" y2="8" stroke="currentColor" strokeWidth="1.5"/><line x1="12" y1="4" x2="9" y2="8" stroke="currentColor" strokeWidth="1.5"/></svg>
            : <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M1 4h2.5L7 1v10L3.5 8H1V4z"/><path d="M9 2.5c1.5.8 2.5 2.3 2.5 3.5S10.5 8.7 9 9.5" stroke="currentColor" strokeWidth="1.3" fill="none"/></svg>
          }
        </button>
        <span className="video-ctrl-label">{muted ? 'MUTED' : 'LIVE'}</span>
      </div>
    </div>
  )
}

// 3D tilt cert card (unchanged)
function TiltCertCard() {
  const cardRef = useRef(null)
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 })
  const [hovered, setHovered] = useState(false)

  function onMove(e) {
    const el = cardRef.current
    const rect = el.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width - 0.5
    const y = (e.clientY - rect.top) / rect.height - 0.5
    setTilt({ rx: -y * 18, ry: x * 18 })
  }

  return (
    <div
      ref={cardRef}
      className={`cert-tilt-wrap ${hovered ? 'hovered' : ''}`}
      onMouseMove={onMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setTilt({ rx: 0, ry: 0 }); setHovered(false) }}
      style={{ transform: `perspective(800px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)` }}
    >
      <div className="cert-mock-card">
        <div className="cert-mock-shine" style={{
          background: `radial-gradient(circle at ${(tilt.ry / 18 + 0.5) * 100}% ${(-tilt.rx / 18 + 0.5) * 100}%, rgba(200,245,90,0.13) 0%, transparent 65%)`
        }} />
        <div className="cert-mock-header">
          <span className="cert-mock-logo">H</span>
          <span>humanorigin.app</span>
        </div>
        <div className="cert-mock-badge">✓ Human Origin Confirmed</div>
        <div className="cert-mock-title">Untitled — Oil on Canvas</div>
        <div className="cert-mock-creator">by Alexandra K.</div>
        <div className="cert-mock-divider" />
        <div className="cert-mock-id">HO-2025-A4F9E2B1</div>
        <div className="cert-mock-qr">
          <div className="qr-placeholder">
            {Array.from({ length: 25 }).map((_, i) => (
              <div key={i} className={`qr-cell ${Math.random() > 0.5 ? 'filled' : ''}`} />
            ))}
          </div>
        </div>
        <div className="cert-mock-verified-by">Reviewed by: HumanOrigin team</div>
      </div>
    </div>
  )
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(o => !o)}>
      <div className="faq-q">
        <span>{q}</span>
        <span className="faq-arrow">{open ? '−' : '+'}</span>
      </div>
      {open && <p className="faq-a">{a}</p>}
    </div>
  )
}

function useReveal() {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect() }
    }, { threshold: 0.12 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return [ref, visible]
}

export default function Home() {
  const { user } = useAuth()
  const primaryCta = user ? '/project/new' : '/login'
  const finalCta = user ? '/dashboard' : '/login'
  const finalLabel = user ? 'Go to dashboard' : 'Start for free'

  const [howRef, howVisible] = useReveal()
  const [certRef, certVisible] = useReveal()
  const [faqRef, faqVisible] = useReveal()

  return (
    <div className="home page-fade">

      {/* ── HERO — video background ── */}
      <section className="hero">
        <HeroVideo />
        <div className="hero-content container">
          <div className="hero-eyebrow">
            <span className="eyebrow-dot" />
            Verification for the AI age
          </div>
          <h1 className="hero-title">
            Your work is human.<br />
            <span className="hero-accent">Now prove it.</span>
          </h1>
          <p className="hero-sub">
            HumanOrigin reviews your process evidence and issues a permanent
            certificate — a record no AI can fake.
          </p>
          <div className="hero-actions">
            <Link to={primaryCta} className="btn-hero-primary">
              Get verified
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </Link>
            <Link to="/browse" className="btn-hero-ghost">Browse creators</Link>
          </div>
        </div>
      </section>

      {/* ── BRAND MARQUEE RIBBON ── */}
      <div className="marquee-strip">
        <div className="marquee-strip-label">Trusted by creators from</div>
        <div className="marquee-track-wrap">
          <div className="marquee-track">
            {[...brands, ...brands].map((b, i) => (
              <span key={i} className="marquee-item">
                <img
                  src={logoUrl(b.domain)}
                  alt={b.name}
                  className="marquee-logo"
                  onError={e => { e.currentTarget.style.display = 'none' }}
                />
                {b.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <section className="section-how" ref={howRef}>
        <div className="container">
          <div className="section-label">How it works</div>
          <h2 className="section-title">Three steps.<br />One permanent record.</h2>
        </div>
        <div className={`how-steps ${howVisible ? 'revealed' : ''}`}>
          <div className="how-step" style={{ '--delay': '0ms' }}>
            <div className="how-step-num">01</div>
            <div className="how-step-body">
              <h3>Submit your work</h3>
              <p>Upload details about your creation and link process evidence — videos, raw files, drafts, anything that shows the creative process.</p>
            </div>
            <div className="how-step-icon">📁</div>
          </div>
          <div className="how-step-connector"><div className="connector-line" /><div className="connector-dot" /></div>
          <div className="how-step" style={{ '--delay': '120ms' }}>
            <div className="how-step-num">02</div>
            <div className="how-step-body">
              <h3>Community review</h3>
              <p>Real people — other creators, not a paid admin team — review your evidence trail and vote on whether it's genuine. No algorithms, no automated gatekeeping.</p>
            </div>
            <div className="how-step-icon">👁</div>
          </div>
          <div className="how-step-connector"><div className="connector-line" /><div className="connector-dot" /></div>
          <div className="how-step" style={{ '--delay': '240ms' }}>
            <div className="how-step-num">03</div>
            <div className="how-step-body">
              <h3>Get your certificate</h3>
              <p>Receive a unique Human Origin certificate with QR code. Permanent, public, shareable anywhere.</p>
            </div>
            <div className="how-step-icon">🏅</div>
          </div>
        </div>
      </section>

      {/* ── CERT SECTION ── */}
      <section className="section-cert" ref={certRef}>
        <div className={`container cert-layout ${certVisible ? 'revealed' : ''}`}>
          <div className="cert-text">
            <div className="section-label">The certificate</div>
            <h2 className="section-title" style={{ textAlign: 'left' }}>
              A record worth<br />showing off.
            </h2>
            <p>Each verified creation gets its own certificate page — like a diploma, but for your art. Share it on Instagram, link it from your portfolio, print it for exhibitions.</p>
            <ul className="cert-features">
              <li>Unique ID & scannable QR code</li>
              <li>Creator name & reviewer signature</li>
              <li>Optional evidence timeline display</li>
              <li>Print-ready layout</li>
              <li>Never expires</li>
            </ul>
            <Link to={primaryCta} className="btn-hero-primary" style={{ marginTop: 32, display: 'inline-flex' }}>
              Create yours
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </Link>
          </div>
          <TiltCertCard />
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="section-faq" ref={faqRef}>
        <div className={`container ${faqVisible ? 'revealed' : ''}`}>
          <div className="section-label">FAQ</div>
          <h2 className="section-title">Questions</h2>
          <div className="faq-list">
            {faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
          </div>
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Link to="/faq" className="btn-hero-ghost">All FAQs</Link>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="section-cta">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-glow" />
            <div className="cta-text">
              <h2>Your work deserves proof.</h2>
              <p>Start free. Get your first verification in minutes.</p>
            </div>
            <Link to={finalCta} className="btn-hero-primary btn-hero-large">
              {finalLabel}
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-brand">
            <span className="logo-mark" style={{ width: 26, height: 26, fontSize: 13 }}>H</span>
            <span className="logo-text" style={{ fontSize: 13 }}>humanorigin.app</span>
          </div>
          <div className="footer-links">
            <Link to="/wall">Wall</Link>
            <Link to="/browse">Browse</Link>
            <Link to="/faq">FAQ</Link>
            <Link to={finalCta}>{user ? 'Dashboard' : 'Sign in'}</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <p className="footer-copy">© 2026 HumanOrigin. Built to protect human creativity.</p>
        </div>
      </footer>

    </div>
  )
}
